import { buildPaxSlotsForLdsSupplementsFromSearchRooms } from "./flightOfferAdapter.js";

/**
 * בונה selectedResult + roomsAndPaxInfo כך ש-calculateLdsTotalPayment ב-SelectedResultContext
 * ירוץ כמו בזרימת חבילות: לכל חדר מ-roomInfo יש room.ourFareDetails.ourTotalFare מתוך
 * getLdsDealRoomFareRows (אותו מיפוי כמו calculateLDSDealFareForRoomsComposition).
 *
 * @param {object} params
 * @param {object} params.savedDeal — deal מ-searchFlights (כולל dealFare, fareDetails, linkedFlights, supplement)
 * @param {object} [params.roomInfo] — { rooms: [...] } מ-SearchContext
 * @param {object} [params.linkedFlightsOverride] — אם לא מועבר, נלקח מ-savedDeal.linkedFlights
 * @param {object|null} [params.shortenedDeal]
 * @param {function} params.getLdsDealRoomFareRows — מ-SearchContext
 * @param {function} params.calculateLDSDealFareForRoomsComposition — מ-SearchContext
 * @returns {{ ok: true, selectedResult: object, roomsAndPaxInfo: object[] } | { ok: false, error: string, details?: object }}
 */
export function buildSelectedResultForLdsFlightBooking({
  savedDeal,
  roomInfo,
  linkedFlightsOverride,
  shortenedDeal = null,
  getLdsDealRoomFareRows,
  calculateLDSDealFareForRoomsComposition,
}) {
  if (!savedDeal || typeof savedDeal !== "object") {
    return { ok: false, error: "missing_savedDeal" };
  }
  if (typeof getLdsDealRoomFareRows !== "function") {
    return { ok: false, error: "missing_getLdsDealRoomFareRows" };
  }
  if (typeof calculateLDSDealFareForRoomsComposition !== "function") {
    return { ok: false, error: "missing_calculateLDSDealFareForRoomsComposition" };
  }

  const fareRows = getLdsDealRoomFareRows(savedDeal, shortenedDeal);
  if (!Array.isArray(fareRows) || fareRows.length === 0) {
    return {
      ok: false,
      error: "no_fare_rows",
      details: { dealId: savedDeal.dealId ?? savedDeal.ID },
    };
  }

  const slots = buildPaxSlotsForLdsSupplementsFromSearchRooms(
    roomInfo?.rooms
  );

  const roomsAndPaxInfo = fareRows.map((row, i) => {
    const slot = slots[i] || {
      adults: [{ firstname: null, lastname: null }],
      kids: [],
      infants: [],
      room: null,
    };
    if (!row?.ourFareDetails?.ourTotalFare) {
      return null;
    }
    return {
      adults: slot.adults,
      kids: slot.kids,
      infants: slot.infants,
      room: {
        ourFareDetails: row.ourFareDetails,
      },
    };
  });

  if (roomsAndPaxInfo.some((r) => r == null)) {
    return {
      ok: false,
      error: "incomplete_fare_row",
      details: { fareRowCount: fareRows.length },
    };
  }

  const ourPrice = calculateLDSDealFareForRoomsComposition(
    savedDeal,
    shortenedDeal
  );
  if (!ourPrice?.ourTotalFare) {
    return {
      ok: false,
      error: "no_aggregate_ourPrice",
      details: { dealId: savedDeal.dealId ?? savedDeal.ID },
    };
  }

  const dealId = savedDeal.dealId ?? savedDeal.ID;
  const selectedResult = {
    dealId,
    dealType: savedDeal.dealType || "Flights",
    supplement: savedDeal.supplement,
    linkedFlights:
      linkedFlightsOverride !== undefined
        ? linkedFlightsOverride
        : savedDeal.linkedFlights,
    ourPrice,
    shortenedDeal: shortenedDeal ?? null,
  };

  return {
    ok: true,
    selectedResult,
    roomsAndPaxInfo,
  };
}
